import { createElement, useState, type ComponentType } from "react";
import { trackEvent } from "../utils/analytics";

type PdfStatus = "idle" | "generating" | "error";

/**
 * Shared by About.tsx (company profile) and Sustainability.tsx (impact
 * report) — both need the identical dynamic-import → render-to-blob →
 * anchor-click-download sequence, differing only in which PDF document to
 * load, what to name the file, and which analytics event to fire.
 */
export default function usePdfDownload(
  loadDocument: () => Promise<{ default: ComponentType }>,
  filename: string,
  analyticsEvent: string
) {
  const [status, setStatus] = useState<PdfStatus>("idle");

  const download = async () => {
    setStatus("generating");
    try {
      const [{ pdf }, { default: Document }] = await Promise.all([
        import("@react-pdf/renderer"),
        loadDocument(),
      ]);
      const blob = await pdf(createElement(Document)).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      trackEvent(analyticsEvent);
      setStatus("idle");
    } catch {
      setStatus("error");
    }
  };

  return { status, download };
}
